import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { QACommunityBanner } from "../components/QACommunity";
import { toast } from "sonner";
import {
  Copy, Check, Calendar, ExternalLink, Wallet, MessageCircle,
  Share2, Lightbulb, Megaphone, ChevronDown, FileText, Users, TrendingUp,
  Image as ImageIcon, Download, Loader2, Trash2, Edit2, Save, RotateCcw, X as XIcon,
} from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { isPartner, isAdmin, getAuthHeaders, logout, loginUrl } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const WEBSITE = "https://alphalane-tradingsystems.com";

// Generic insider-report fallback URL — used when a partner has no slug yet.
// When a slug exists, the link is auto-built per partner: `${WEBSITE}/ai/${slug}`
// (auto-attributing lead-magnet route).
const INSIDER_LINK_GENERIC = `${WEBSITE}/insider-report`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", { dateStyle: "full", timeStyle: "short" });
  } catch { return iso; }
};

const fmtMoney = (n) => {
  if (n == null) return "—";
  return n.toLocaleString("de-CH", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

// ===== Social Media Templates =====
// Factory: produces the platform-grouped templates with the partner's own
// auto-attributing lead-magnet link substituted in (`/ai/${slug}`). When a
// partner doesn't have a slug yet we fall back to the generic /insider-report.
//
// Each post carries a stable ``id`` ('wa_0', 'li_1', …) so a partner can
// persist their personal override and we can map it back at render time.
const buildSocialTemplates = (insiderLink) => [
  {
    platform: "WhatsApp / Telegram",
    icon: MessageCircle,
    color: "from-emerald-500 to-emerald-600",
    posts: [
      {
        id: "wa_0",
        title: "Erst-Kontakt — kurz und neugierig",
        text: `Hey [Name] 👋

Schau mal — ich teste seit ein paar Wochen ein System, das automatisch tradet (ohne dass ich was tun muss). Bisher echt überzeugend.

Falls dich Trading mit KI interessiert: am 31. Mai zeigt Pascal das live in einem kostenlosen Webinar. Hier kannst du dir den kostenlosen Insider-Report holen + dich anmelden:

${insiderLink}

Kein Pflicht-Programm — nur falls du Bock hast 🤙`,
      },
      {
        id: "wa_1",
        title: "Nach erstem Gespräch — Follow-up",
        text: `Hey [Name], wie versprochen — hier der Link zum Insider-Report über das Trading-System:

${insiderLink}

PDF + Webinar-Anmeldung in 30 Sek. Bei Fragen — meld dich einfach.`,
      },
    ],
  },
  {
    platform: "LinkedIn",
    icon: Users,
    color: "from-sky-500 to-blue-700",
    posts: [
      {
        id: "li_0",
        title: "Erfahrungsbericht (subtil)",
        text: `Was die meisten Trading-„Gurus" auf LinkedIn nicht sagen:

Die echten Profis lassen ihr Geld nicht im Chart staren — sie lassen es laufen.

Seit kurzem läuft ein KI-gestütztes Trading-System auf meinem Broker-Konto. Vollautomatisch. Keine Signale, keine Charts, kein Emo-Trading. Ich sehe nur das Resultat im Dashboard.

Spannend ist nicht die Rendite — sondern dass ich nicht mehr darüber nachdenken muss.

Wer wissen will, wie das technisch funktioniert: Pascal Schwab macht am 31. Mai ein Live-Webinar dazu. Link in den Kommentaren.

#trading #ki #fintech #passiveeinkommen`,
      },
      {
        id: "li_1",
        title: "Fachpost — Hedge-Fund vs. Privatanleger",
        text: `Warum verlieren 90% der Privatanleger Geld an der Börse?

Ganz simpel: sie kämpfen mit Bauchgefühl gegen Algorithmen.

Hedge-Funds traden seit Jahren vollautomatisch — mit Strategien, die Privatanleger nie zu sehen bekommen. Bis jetzt.

Alphalane macht Hedge-Fund-Strategien für Retail zugänglich. Kein Hype-Crypto-Kurs, sondern systematisches Trading mit klarer Methodik.

Am 31. Mai erklärt Gründer Pascal Schwab das Konzept live. Wer Lust hat reinzuhören:

${insiderLink}`,
      },
    ],
  },
  {
    platform: "Instagram / Story",
    icon: Share2,
    color: "from-fuchsia-500 to-pink-600",
    posts: [
      {
        id: "ig_0",
        title: "Story-Slide 1 — Hook",
        text: `📈 Was wenn dein Geld 24/7 für dich arbeitet?

Stell dir vor: KI-Trading-System auf deinem eigenen Broker-Konto. Du verbindest es einmal — und siehst nur noch die Resultate im Dashboard.

Klingt zu schön um wahr zu sein? Schau dir das Webinar an. Link in Bio.

#trading #passiveincome #ki`,
      },
      {
        id: "ig_1",
        title: "Story-Slide 2 — Call-to-Action",
        text: `🚀 Kostenloses Webinar am 31. Mai

— Wie das Alphalane KI-System wirklich funktioniert
— Echte Zahlen, kein Hype
— Live-Q&A mit Pascal Schwab

Hier kostenlos anmelden 👆 (Swipe up / Link in Bio)
${insiderLink}`,
      },
    ],
  },
  {
    platform: "E-Mail-Signatur",
    icon: FileText,
    color: "from-slate-700 to-slate-900",
    posts: [
      {
        id: "email_0",
        title: "Footer in deinen E-Mails",
        text: `--
P.S. Ich teste seit kurzem ein KI-Trading-System.
Wer wissen will wie das funktioniert: ${insiderLink}`,
      },
    ],
  },
];

// ===== Einwand-Bibliothek =====
const OBJECTIONS = [
  {
    objection: '„Ich überleg\'s mir und melde mich"',
    answer: `Klar — verständlich. Damit du beim Überlegen alle Fakten hast: was hindert dich konkret heute? In den meisten Fällen geht's um eine der drei Sachen: Sicherheit deines Geldes, Höhe des Startkapitals, oder einfach Unsicherheit beim Setup. Welcher Punkt ist's bei dir?

→ Konkret nachfragen verhindert „Ich ruf zurück". 80% melden sich sonst nie wieder.`,
  },
  {
    objection: '„Ist das nicht zu riskant?"',
    answer: `Berechtigte Frage. Drei Punkte dazu:

1. Dein Geld liegt auf deinem Broker-Konto (RoboForex, reguliert) — wir haben null Auszahlungsrecht, nur Read-Only + Handelsausführung.
2. Das System hat hartes Risikomanagement: pro Trade fixe Stop-Loss-Levels, Diversifikation über mehrere Märkte.
3. Drawdowns gibt's — das ist normal. Im Webinar zeigt Pascal genau den historischen Max-Drawdown, damit du selbst entscheiden kannst.

Schau dir das Webinar an, dann kannst du wirklich fundiert entscheiden.`,
  },
  {
    objection: '„Was wenn ich verliere?"',
    answer: `Nüchtern: bei jedem Trading-System gibt's Verlustphasen. Wichtig:
— Du setzt NUR Kapital ein, dessen Verlust verkraftbar wäre.
— In Verlustmonaten zahlst du 0 Performance Fee (40% gelten nur auf Gewinne).
— Du kannst jederzeit aussteigen (kein Vertrag, keine Frist) — 1 Klick beim Broker.

Genau deshalb empfehlen wir 2-5k USD zum Start. Spielst du, schaust du. Funktioniert es, kannst du immer skalieren.`,
  },
  {
    objection: '„40% sind viel"',
    answer: `Im ersten Moment ja — aber rechne mal:
— Klassischer Hedge Fund: 2% Verwaltung + 20% Performance. Du zahlst auch in Verlustjahren.
— Bei uns: 0% Fix. 40% nur auf Gewinne.
— Bei 10% Monatsrendite auf 10k: 400 USD an uns, 600 USD bleibt bei dir — sonst hättest du gar nichts gehabt.

Wir verdienen NUR, wenn DU verdienst. Das ist Skin-in-the-Game.`,
  },
  {
    objection: '„Klingt nach Schneeballsystem"',
    answer: `Komplett verstehe ich die Skepsis. Aber:
— Dein Geld kommt nie zu Alphalane — es bleibt auf DEINEM Broker-Konto auf DEINEN Namen.
— Es gibt KEINE Einzahlung an uns. Wir verdienen nur an Performance.
— Du kannst jederzeit auszahlen direkt beim Broker — komplett unabhängig von uns.
— Schau dir RoboForex an (regulierter Broker), schau dir den Track Record an, sprich mit Pascal selbst — alles transparent.`,
  },
  {
    objection: '„Ich kenn mich nicht mit Trading aus"',
    answer: `Perfekt — musst du auch nicht. Das ist ja der Punkt:
— Du musst keinen einzigen Trade selbst platzieren.
— Du musst MT5 nicht verstehen — wir verbinden das für dich.
— Setup dauert 30-45 Min, wir helfen Schritt für Schritt.

Du brauchst nur 3 Sachen: ein Broker-Konto (machen wir gemeinsam), Startkapital, eine E-Mail-Adresse fürs Dashboard. Mehr nicht.`,
  },
  {
    objection: '„Erst muss ich meinen Berater fragen"',
    answer: `Sehr vernünftig. Zwei Tipps für das Gespräch:
1. Zeig deinem Berater den Track Record und die Cost-Structure — viele Berater kennen das Modell nicht, sind aber überzeugt wenn sie die Zahlen sehen.
2. Frag ihn explizit: „Welches Risiko siehst du KONKRET?" Vage Antworten → fragwürdig. Konkrete Antworten → wichtig zu wissen.

Falls dein Berater Fragen hat: gib ihm Pascals Nummer. Er beantwortet alles direkt.`,
  },
  {
    objection: '„Was wenn die Firma pleite geht?"',
    answer: `Top-Frage. Antwort: dein Geld ist NICHT bei Alphalane.
— Dein Kapital liegt auf deinem Broker-Konto (RoboForex). Wenn Alphalane verschwinden würde — dein Geld bleibt da, du widerrufst einfach die Handelsvollmacht und alles ist deins.
— Die KI läuft auf redundanten Servern; bei Auffälligkeiten gibt's einen automatischen Notabschaltungs-Mechanismus.
— Anders gesagt: das einzige was du verlieren kannst, ist die Performance-Fee — nicht dein Kapital.`,
  },
  {
    objection: '„Mein Freund hat aber gesagt..."',
    answer: `Was hat er gesagt? (Konkret nachfragen.)
Häufig sind das vage Bedenken, die wir leicht auflösen können. Schick deinen Freund einfach das Webinar — wenn er ehrlich kritisch denkt, wird er nach 45 Min selbst überzeugt sein.

Wichtig: vertraue mehr deiner eigenen Recherche als Hörensagen. Das ist deine Entscheidung über dein Geld.`,
  },
];

// ===== Helpful Tips =====
const HOT_WORDS = {
  avoid: ["garantierte Rendite", "risikofrei", "100% sicher", "schnell reich", "kein Risiko"],
  use: ["systematisches Trading", "diszipliniertes Risikomanagement", "transparente Zahlen", "regulierter Broker", "jederzeit kündbar"],
};

// ===== Photo Templates (high-res social media banners/posts) =====
const PHOTO_TEMPLATES = [
  { label: "4-Grid — Performance & Engines", url: "/social-templates/template-01.png", aspect: "aspect-square" },
  { label: "Hedge-Fund-Strategien (Banner)", url: "/social-templates/template-02.png", aspect: "aspect-square", fit: "contain" },
  { label: "Quant-Strategien für Privat-Anleger", url: "/social-templates/template-03.png", aspect: "aspect-square" },
  { label: "Logik statt Emotion", url: "/social-templates/template-04.jpeg", aspect: "aspect-square" },
  { label: "David gegen 1000 Goliaths", url: "/social-templates/template-05.png", aspect: "aspect-square" },
  { label: "Alphalane AI — Hedge-Fund-Strategien", url: "/social-templates/template-06.png", aspect: "aspect-square" },
  { label: "Hedge-Fund-Strategien (Plexus)", url: "/social-templates/template-07.png", aspect: "aspect-square" },
  { label: "90% Psychologie — Aber nur wenn du selbst handelst", url: "/social-templates/template-08.png", aspect: "aspect-square" },
  { label: "Unser Versprechen — 0€ Fixkosten", url: "/social-templates/template-09.png", aspect: "aspect-square" },
  { label: "0 Min. mit Charts — KI handelt für dich", url: "/social-templates/template-10.png", aspect: "aspect-square" },
  { label: "Alphalane Logo (Schwarz)", url: "/social-templates/template-11.png", aspect: "aspect-square" },
  { label: "LIVE — Track Record tagesaktuell", url: "/social-templates/template-12.jpeg", aspect: "aspect-square" },
  { label: "Jetzt verfügbar — Jetzt anfragen", url: "/social-templates/template-13.jpeg", aspect: "aspect-square" },
  { label: "Alphalane Trading Systems (Light)", url: "/social-templates/template-14.jpeg", aspect: "aspect-square" },
  { label: "Was wäre wenn — du gar nicht handeln müsstest?", url: "/social-templates/template-15.jpeg", aspect: "aspect-square" },
  { label: "Wusstest du? 90% verlieren beim Trading", url: "/social-templates/template-16.jpeg", aspect: "aspect-square" },
  { label: "Bald kommt etwas — die Schweiz noch nicht gesehen", url: "/social-templates/template-17.jpeg", aspect: "aspect-square" },
  { label: "Alphalane Trading Systems Logo (Hero)", url: "/social-templates/template-18.png", aspect: "aspect-square" },
  { label: "Alphalane AI Logo", url: "/social-templates/template-19.png", aspect: "aspect-square" },
];

export default function PartnerCoaching() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [treeStats, setTreeStats] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [copied, setCopied] = useState(null);
  const [openSocial, setOpenSocial] = useState(0);
  const [openObjection, setOpenObjection] = useState(null);
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  // Per-partner overrides for the social-media text templates, keyed by stable ID
  // (wa_0, li_1, …). Hydrated from /partner/stats, mutated via PATCH /partner/social-templates.
  const [socialOverrides, setSocialOverrides] = useState({});
  // System-wide defaults that the admin can edit. Both admin and partner views fetch this.
  // Effective text: partner override > admin default > hard-coded fallback.
  const [systemDefaults, setSystemDefaults] = useState({});
  // Inline-edit state — only one template is editable at a time.
  const [editingTplId, setEditingTplId] = useState(null);
  const [editingTplDraft, setEditingTplDraft] = useState("");
  const [editingTplBusy, setEditingTplBusy] = useState(false);

  const fetchAvailableTemplates = async () => {
    try {
      const r = await axios.get(`${API}/social-templates`);
      setAvailableTemplates(new Set(r.data?.files || []));
    } catch {
      // Listing endpoint down → fall back to showing all hardcoded templates.
      setAvailableTemplates(null);
    }
  };

  useEffect(() => { fetchAvailableTemplates(); }, []);

  const deleteTemplate = async (url, label) => {
    const filename = url.split("/").pop();
    if (!window.confirm(`Template löschen?\n\n"${label}"\n(${filename})\n\nDiese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setDeletingTemplate(filename);
    try {
      await axios.delete(`${API}/admin/social-templates/${filename}`, { headers: getAuthHeaders() });
      toast.success(`✓ "${label}" gelöscht`);
      await fetchAvailableTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen.");
    } finally {
      setDeletingTemplate(null);
    }
  };

  useEffect(() => {
    if (!isPartner() && !isAdmin()) {
      navigate(loginUrl());
      return;
    }
    (async () => {
      try {
        const [s, w, d] = await Promise.all([
          isPartner()
            ? axios.get(`${API}/partner/stats`, { headers: getAuthHeaders() })
            : Promise.resolve({ data: { partner: null, stats: null } }),
          axios.get(`${API}/webinars/upcoming?limit=10`),
          axios.get(`${API}/social-templates/defaults`),
        ]);
        setMeta(s.data.partner);
        setStats(s.data.stats);
        setTreeStats(s.data.tree_stats || null);
        setWebinars(w.data.webinars || []);
        setSocialOverrides(s.data.partner?.social_templates || {});
        setSystemDefaults(d.data?.templates || {});
      } catch (e) {
        if (e.response?.status === 401) {
          const t = loginUrl(); logout(); navigate(t);
        }
      }
    })();
  }, [navigate]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("In die Zwischenablage kopiert!");
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error("Konnte nicht kopieren.");
    }
  };

  const startEditTpl = (id, currentText) => {
    setEditingTplId(id);
    setEditingTplDraft(currentText);
  };

  const cancelEditTpl = () => {
    setEditingTplId(null);
    setEditingTplDraft("");
  };

  const saveEditTpl = async (id) => {
    if (!isPartner() && !isAdmin()) {
      toast.info("Anpassen der Vorlagen ist nur für eingeloggte Partner oder Admins möglich.");
      cancelEditTpl();
      return;
    }
    setEditingTplBusy(true);
    try {
      // Admin → edits the SYSTEM-WIDE default (every partner sees it unless they override).
      // Partner → edits their PERSONAL override (only they see it).
      const endpoint = isAdmin() && !isPartner()
        ? `${API}/admin/social-templates/defaults`
        : `${API}/partner/social-templates`;
      const res = await axios.patch(
        endpoint,
        { key: id, text: editingTplDraft },
        { headers: getAuthHeaders() },
      );
      if (isAdmin() && !isPartner()) {
        setSystemDefaults(res.data?.templates || {});
        toast.success("System-Default-Vorlage gespeichert (für alle Partner sichtbar).");
      } else {
        setSocialOverrides(res.data?.social_templates || {});
        toast.success("Vorlage gespeichert.");
      }
      cancelEditTpl();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setEditingTplBusy(false);
    }
  };

  const resetEditTpl = async (id) => {
    if (!isPartner() && !isAdmin()) return;
    const isAdminOnly = isAdmin() && !isPartner();
    const msg = isAdminOnly
      ? "System-Default auf Hard-Code-Fallback zurücksetzen? Alle Partner sehen wieder die ursprüngliche Vorlage (sofern sie keine eigene Version haben)."
      : "Auf Standard-Vorlage zurücksetzen? Deine Anpassung geht verloren.";
    if (!window.confirm(msg)) return;
    setEditingTplBusy(true);
    try {
      const endpoint = isAdminOnly
        ? `${API}/admin/social-templates/defaults`
        : `${API}/partner/social-templates`;
      const res = await axios.patch(
        endpoint,
        { key: id, text: null },
        { headers: getAuthHeaders() },
      );
      if (isAdminOnly) {
        setSystemDefaults(res.data?.templates || {});
        toast.success("System-Default zurückgesetzt.");
      } else {
        setSocialOverrides(res.data?.social_templates || {});
        toast.success("Auf Standard zurückgesetzt.");
      }
      cancelEditTpl();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fehlgeschlagen.");
    } finally {
      setEditingTplBusy(false);
    }
  };

  const insiderCopy = useMemo(
    () => (meta?.slug ? `${WEBSITE}/ai/${meta.slug}` : INSIDER_LINK_GENERIC),
    [meta?.slug]
  );
  const socialTemplates = useMemo(() => buildSocialTemplates(insiderCopy), [insiderCopy]);

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="partner-coaching-page">
      <AdminTopNav />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 space-y-10">
        <QACommunityBanner storageKey="qa-banner-partner-dismissed" />
        {/* Hero */}
        <header>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            Coaching <span className="al-grad-text">& Material</span>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Alles, was du brauchst, um Leads erfolgreich zum Webinar zu bringen — Social-Media-Vorlagen, Antworten auf häufige Einwände, Webinar-Termine, und dein aktueller Pipeline-Wert.
          </p>
        </header>

        {/* Quick stats — Kunden-Volumen */}
        {stats && (
          <section className="grid sm:grid-cols-2 gap-3" data-testid="coaching-stats">
            <StatCard
              icon={Users}
              label="Aktive Kunden"
              value={stats.customers || 0}
              trend={
                treeStats?.new_customers_30d > 0
                  ? `+${treeStats.new_customers_30d} neue in 30 Tagen`
                  : null
              }
              tone="violet"
            />
            <StatCard
              icon={Wallet}
              label="Aktuelles Pipeline-Volumen"
              value={fmtMoney((treeStats?.total_balance ?? stats.total_balance) || 0)}
              trend={
                treeStats?.new_deposits_7d > 0
                  ? `+${fmtMoney(treeStats.new_deposits_7d)} Einzahlungen (7 Tage)`
                  : null
              }
              hint={
                (() => {
                  const withLive = treeStats?.customers_with_live_data ?? stats.customers_with_live_data ?? 0;
                  const totalCust = treeStats?.customers ?? stats.customers ?? 0;
                  if (withLive > 0) return `${withLive} von ${totalCust} mit Live-Daten · gesamte Struktur`;
                  return "Gesamtvolumen aller Kunden in deiner Struktur";
                })()
              }
              tone="emerald"
            />
          </section>
        )}

        {/* Insider-Report-Link Helper */}
        <section className="al-card p-6 bg-gradient-to-br from-violet-50/60 to-white" data-testid="insider-link-helper">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[var(--al-purple)] to-fuchsia-500 flex items-center justify-center shadow-md">
              <Lightbulb className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg">Schicke deinen Kunden DIESEN Link</h2>
              <p className="text-xs text-slate-500">Auf der Seite holen sie sich den Insider-Report und melden sich automatisch zum Webinar an.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 p-3 rounded-xl bg-white border border-violet-200">
            <code className="flex-1 text-sm font-mono text-slate-800 truncate" data-testid="insider-link">{insiderCopy}</code>
            <button
              onClick={() => copy(insiderCopy, "insider")}
              data-testid="insider-link-copy"
              className="px-3 py-1.5 rounded-lg bg-[var(--al-purple)] hover:bg-violet-700 text-white text-sm font-display font-bold transition-colors flex items-center gap-1.5"
            >
              {copied === "insider" ? <><Check className="h-3.5 w-3.5" /> Kopiert</> : <><Copy className="h-3.5 w-3.5" /> Kopieren</>}
            </button>
            <a
              href={insiderCopy}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-lg border border-violet-200 hover:bg-violet-50 text-sm flex items-center gap-1.5"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Öffnen
            </a>
          </div>
        </section>

        {/* Verkaufsunterlagen — PDF-Downloads für Kundengespräche */}
        <section className="al-card p-6" data-testid="partner-downloads-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg">Verkaufsunterlagen</h2>
              <p className="text-xs text-slate-500">Druckreife PDFs zum Versenden oder Ausdrucken — perfekt für persönliche Gespräche.</p>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            <li>
              <a
                href="/alphalane-kurzpraesentation.pdf"
                download
                data-testid="download-kurzpraesentation"
                className="group flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="h-10 w-10 shrink-0 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 font-mono text-[10px] font-bold">
                  PDF
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm text-slate-900 truncate">Alphalane Kurzpräsentation 🇩🇪</div>
                  <div className="text-[11px] text-slate-500">Die wichtigsten Zahlen, Strategie &amp; Onboarding kompakt auf wenigen Seiten.</div>
                </div>
                <Download className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 transition-colors shrink-0" />
              </a>
            </li>
            <li>
              <a
                href="/alphalane-customer-info-en.pdf"
                download
                data-testid="download-kurzpraesentation-en"
                className="group flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="h-10 w-10 shrink-0 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 font-mono text-[10px] font-bold">
                  PDF
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm text-slate-900 truncate">Alphalane Customer Info 🇬🇧</div>
                  <div className="text-[11px] text-slate-500">English version — share with international prospects &amp; customers.</div>
                </div>
                <Download className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 transition-colors shrink-0" />
              </a>
            </li>
          </ul>
        </section>

        {/* Webinar-Kalender */}
        <section className="al-card p-6" data-testid="webinar-calendar-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-amber-700" />
              </div>
              <h2 className="font-display font-bold text-lg">Kommende Webinare</h2>
            </div>
            <span className="text-xs text-slate-400">Nur zur Info — Kunden werden automatisch eingeladen.</span>
          </div>

          {/* Anmelde-Links der beiden Webinar-Landingpages */}
          <div className="grid sm:grid-cols-2 gap-2.5 mb-4" data-testid="webinar-landing-links">
            <a
              href="/webinar"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="webinar-landing-link-live"
              className="flex items-center justify-between gap-3 p-3 rounded-xl border border-violet-200 bg-violet-50/50 hover:bg-violet-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm text-slate-900">Live-Webinar (Pascal)</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">Anmeldeseite: {typeof window !== "undefined" ? window.location.origin : ""}/webinar</div>
              </div>
              <ExternalLink className="h-4 w-4 text-violet-600 flex-shrink-0" />
            </a>
            <a
              href="/webinartech"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="webinar-landing-link-tech"
              className="flex items-center justify-between gap-3 p-3 rounded-xl border border-blue-200 bg-blue-50/50 hover:bg-blue-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm text-slate-900">Technik-Webinar (Adrian)</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">Anmeldeseite: {typeof window !== "undefined" ? window.location.origin : ""}/webinartech</div>
              </div>
              <ExternalLink className="h-4 w-4 text-blue-600 flex-shrink-0" />
            </a>
          </div>
          {webinars.length === 0 ? (
            <p className="py-6 text-center text-slate-400 text-sm">Aktuell sind keine Webinare geplant.</p>
          ) : (
            <div className="space-y-2">
              {webinars.map((w) => (
                <div key={w.id} className="p-3 rounded-xl border border-slate-100 hover:border-amber-200 hover:bg-amber-50/30 transition-colors flex items-start justify-between gap-3"
                  data-testid={`webinar-row-${w.slug}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-slate-900 truncate">{w.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{fmtDate(w.scheduled_at)}</div>
                    {w.description && (
                      <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{w.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Social-Media-Vorlagen */}
        <section data-testid="social-templates-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl bg-fuchsia-100 flex items-center justify-center">
              <Megaphone className="h-5 w-5 text-fuchsia-700" />
            </div>
            <h2 className="font-display font-bold text-lg">Social-Media-Vorlagen</h2>
          </div>
          {isPartner() && (
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Klick auf <strong className="font-semibold">Bearbeiten</strong>, um eine Vorlage an deinen Stil anzupassen — die geänderte Version wird automatisch für dich gespeichert und beim nächsten Klick auf „Kopieren" verwendet.
            </p>
          )}
          {isAdmin() && !isPartner() && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 leading-relaxed">
              <strong>Admin-Modus:</strong> Wenn du eine Vorlage bearbeitest, ändert sich die <strong>System-Default-Vorlage für alle Partner</strong> — solange ein Partner keine eigene Version gespeichert hat.
            </p>
          )}
          <div className="space-y-3">
            {socialTemplates.map((cat, i) => {
              const Icon = cat.icon;
              const open = openSocial === i;
              return (
                <div key={cat.platform} className="al-card overflow-hidden" data-testid={`social-cat-${i}`}>
                  <button
                    onClick={() => setOpenSocial(open ? -1 : i)}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center shadow-sm`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="flex-1 font-display font-bold text-base">{cat.platform}</span>
                    <span className="text-xs text-slate-400">{cat.posts.length} Vorlage{cat.posts.length === 1 ? "" : "n"}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 px-5 py-4 space-y-3 bg-slate-50/50">
                      {cat.posts.map((p, pi) => {
                        const partnerOverride = socialOverrides[p.id];
                        const systemOverride = systemDefaults[p.id];
                        const effectiveText = partnerOverride || systemOverride || p.text;
                        const hasPartnerOverride = !!partnerOverride;
                        const hasSystemOverride = !!systemOverride;
                        // What "angepasst" badge to show:
                        //  - Partner sees their own override (if any) → "angepasst"
                        //  - Admin (no partner) sees if system-default is non-stock → "System-Default geändert"
                        const isAdminView = isAdmin() && !isPartner();
                        const isEditing = editingTplId === p.id;
                        return (
                          <div key={p.id || pi} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`social-tpl-${i}-${pi}`}>
                            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                              <div className="font-display font-semibold text-sm flex items-center gap-1.5">
                                {p.title}
                                {!isEditing && hasPartnerOverride && !isAdminView && (
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-violet-700 bg-violet-100 rounded-full px-2 py-0.5" title="Du hast diese Vorlage angepasst">
                                    angepasst
                                  </span>
                                )}
                                {!isEditing && isAdminView && hasSystemOverride && (
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" title="System-Default wurde vom Admin angepasst — alle Partner sehen diese Version">
                                    System-Default
                                  </span>
                                )}
                              </div>
                              {isEditing ? (
                                <div className="flex gap-1.5">
                                  <button
                                    onClick={() => saveEditTpl(p.id)}
                                    disabled={editingTplBusy}
                                    data-testid={`social-tpl-save-${p.id}`}
                                    className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex items-center gap-1 disabled:opacity-50"
                                  >
                                    {editingTplBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Speichern
                                  </button>
                                  {((!isAdminView && hasPartnerOverride) || (isAdminView && hasSystemOverride)) && (
                                    <button
                                      onClick={() => resetEditTpl(p.id)}
                                      disabled={editingTplBusy}
                                      data-testid={`social-tpl-reset-${p.id}`}
                                      title="Auf Standard zurücksetzen"
                                      className="px-2.5 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs flex items-center gap-1 disabled:opacity-50"
                                    >
                                      <RotateCcw className="h-3 w-3" /> Standard
                                    </button>
                                  )}
                                  <button
                                    onClick={cancelEditTpl}
                                    disabled={editingTplBusy}
                                    data-testid={`social-tpl-cancel-${p.id}`}
                                    className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs flex items-center gap-1 disabled:opacity-50"
                                  >
                                    <XIcon className="h-3 w-3" /> Abbrechen
                                  </button>
                                </div>
                              ) : (
                                <div className="flex gap-1.5">
                                  {(isPartner() || isAdmin()) && (
                                    <button
                                      onClick={() => startEditTpl(p.id, effectiveText)}
                                      data-testid={`social-tpl-edit-${p.id}`}
                                      title={isAdminView ? "System-Default-Vorlage bearbeiten (alle Partner sehen die Änderung)" : "Vorlage bearbeiten"}
                                      className="px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-slate-700 hover:text-violet-700 text-xs flex items-center gap-1"
                                    >
                                      <Edit2 className="h-3 w-3" /> Bearbeiten
                                    </button>
                                  )}
                                  <button
                                    onClick={() => copy(effectiveText, `s${i}-${pi}`)}
                                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs flex items-center gap-1"
                                    data-testid={`social-copy-${i}-${pi}`}
                                  >
                                    {copied === `s${i}-${pi}` ? <><Check className="h-3 w-3" /> Kopiert</> : <><Copy className="h-3 w-3" /> Kopieren</>}
                                  </button>
                                </div>
                              )}
                            </div>
                            {isEditing ? (
                              <textarea
                                value={editingTplDraft}
                                onChange={(e) => setEditingTplDraft(e.target.value)}
                                rows={Math.max(6, Math.min(20, (editingTplDraft.match(/\n/g) || []).length + 3))}
                                data-testid={`social-tpl-input-${p.id}`}
                                className="w-full text-xs text-slate-800 leading-relaxed font-sans p-3 rounded-lg border border-violet-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-100 outline-none whitespace-pre-wrap"
                                placeholder={isAdminView ? "System-Default-Vorlage (alle Partner sehen das hier)…" : "Deine persönliche Version dieser Vorlage…"}
                                autoFocus
                              />
                            ) : (
                              <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans leading-relaxed">{effectiveText}</pre>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Social Media Templates (Photos) */}
        <section className="al-card p-6" data-testid="photo-templates-section">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-[var(--al-purple)]" />
              <h2 className="font-display font-bold text-lg">Social Media Templates</h2>
            </div>
            <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400">
              {availableTemplates
                ? `${PHOTO_TEMPLATES.filter((t) => availableTemplates.has(t.url.split("/").pop())).length} Templates`
                : `${PHOTO_TEMPLATES.length} Templates`}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Fertige Posts für Instagram, LinkedIn &amp; Co. — herunterladen, mit deinem Affiliate-Link teilen.
            Klicke auf eine Kachel zum Herunterladen.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              const existing = availableTemplates;
              const filtered = PHOTO_TEMPLATES.filter((t) => {
                if (!existing) return true;
                return existing.has(t.url.split("/").pop());
              });
              const visible = photoExpanded ? filtered : filtered.slice(0, 8);
              return visible.map((t, i) => {
                const filename = t.url.split("/").pop();
                const isDeleting = deletingTemplate === filename;
                return (
                  <div
                    key={t.url}
                    className="group relative block rounded-lg overflow-hidden border border-slate-200 hover:border-[var(--al-purple)] hover:shadow-lg transition-all bg-slate-50"
                    data-testid={`photo-template-${i + 1}`}
                  >
                    <a href={t.url} download className="block">
                      <div className={`${t.aspect} w-full overflow-hidden bg-slate-900`}>
                        <img
                          src={t.url}
                          alt={t.label}
                          loading="lazy"
                          className={`w-full h-full ${t.fit === "contain" ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-3 pointer-events-none">
                        <span className="text-white text-[11px] font-medium leading-tight line-clamp-2 flex-1 pr-2">
                          {t.label}
                        </span>
                        <Download className="h-5 w-5 text-white flex-shrink-0" />
                      </div>
                      <div className="px-2.5 py-1.5 text-[11px] text-slate-600 truncate border-t border-slate-100 bg-white">
                        {t.label}
                      </div>
                    </a>
                    {isAdmin() && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteTemplate(t.url, t.label); }}
                        disabled={isDeleting}
                        data-testid={`delete-photo-template-${filename}`}
                        title="Template löschen"
                        className="absolute top-2 right-2 p-1.5 rounded-md bg-white/90 backdrop-blur text-rose-600 hover:bg-rose-600 hover:text-white shadow-md opacity-0 group-hover:opacity-100 transition-all disabled:opacity-100 disabled:cursor-wait"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                );
              });
            })()}
          </div>
          {(() => {
            const existing = availableTemplates;
            const total = existing
              ? PHOTO_TEMPLATES.filter((t) => existing.has(t.url.split("/").pop())).length
              : PHOTO_TEMPLATES.length;
            return total > 8 && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => setPhotoExpanded((v) => !v)}
                  data-testid="photo-templates-toggle"
                  className="px-5 py-2 rounded-full border border-slate-200 hover:border-[var(--al-purple)] hover:bg-violet-50/40 text-sm font-medium text-slate-700 hover:text-[var(--al-purple)] transition-all"
                >
                  {photoExpanded
                    ? "Weniger anzeigen"
                    : `+ ${total - 8} weitere Templates anzeigen`}
                </button>
              </div>
            );
          })()}
        </section>

        {/* Wording-Tipps */}
        <section className="al-card p-6 bg-gradient-to-br from-slate-50 to-white" data-testid="hot-words-section">
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" /> Wording-Tipps
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <h3 className="font-display font-bold text-rose-700 text-xs uppercase tracking-widest mb-2">❌ NICHT verwenden</h3>
              <ul className="space-y-1.5 text-slate-700">
                {HOT_WORDS.avoid.map((w) => (
                  <li key={w} className="flex items-start gap-2">
                    <span className="text-rose-500 mt-1">•</span>
                    <span className="line-through opacity-70">{w}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">Diese Worte sind in CH/DE/AT bei Finanzwerbung problematisch und können dich (und uns) rechtlich gefährden.</p>
            </div>
            <div>
              <h3 className="font-display font-bold text-emerald-700 text-xs uppercase tracking-widest mb-2">✅ STATTDESSEN nutzen</h3>
              <ul className="space-y-1.5 text-slate-700">
                {HOT_WORDS.use.map((w) => (
                  <li key={w} className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-1">•</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">Diese Formulierungen sind ehrlich, transparent und compliant — und wirken bei kritischen Leads sogar überzeugender.</p>
            </div>
          </div>
        </section>

        {/* Einwand-Bibliothek */}
        <section data-testid="objections-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-rose-700" />
            </div>
            <h2 className="font-display font-bold text-lg">Einwand-Bibliothek</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Häufige Einwände + getestete Antworten. Tipp: lieber persönlich anpassen statt 1:1 reinkopieren — wirkt authentischer.</p>
          <div className="space-y-2">
            {OBJECTIONS.map((o, i) => {
              const open = openObjection === i;
              return (
                <div key={o.objection} className="al-card overflow-hidden" data-testid={`objection-${i}`}>
                  <button
                    onClick={() => setOpenObjection(open ? -1 : i)}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="flex-1 font-display font-semibold text-sm text-slate-800">{o.objection}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 px-5 py-4 bg-rose-50/30">
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">{o.answer}</pre>
                      <button
                        onClick={() => copy(o.answer, `o${i}`)}
                        className="mt-3 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs flex items-center gap-1.5"
                        data-testid={`objection-copy-${i}`}
                      >
                        {copied === `o${i}` ? <><Check className="h-3 w-3" /> Kopiert</> : <><Copy className="h-3 w-3" /> Antwort kopieren</>}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div className="text-center pt-4">
          <Link to="/admin/links" className="text-sm text-[var(--al-purple)] hover:underline" data-testid="link-to-assets">
            ← Zurück zu Assets &amp; Reflinks
          </Link>
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, hint, trend, tone = "violet" }) => {
  const toneMap = {
    violet: "from-violet-50 to-white border-violet-200 text-[var(--al-purple)]",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
  };
  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-4 ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold opacity-80 mb-2">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-2xl sm:text-3xl font-mono-tab font-black text-slate-900 leading-none">{value}</div>
      {trend && (
        <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold" data-testid={`stat-trend-${label}`}>
          <TrendingUp className="h-3 w-3" /> {trend}
        </div>
      )}
      {hint && <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
};
